import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { usePermV2 } from '../../../lib/perm_v2';
// Sidebar2 / Header2 (legacy) preserved on disk for rollback — V4 is the default
import DnaSidebarV4 from '../../../design-system/DnaSidebarV4';
import DnaHeaderV4 from '../../../design-system/DnaHeaderV4';
import DnaSpotlightV4 from '../../../design-system/DnaSpotlightV4';
import { cn } from '../../../lib/utils';
import { isAuthenticated } from '../../../lib/auth';
import { useIsMobile } from '../../DnaCommandCenter';
import AdminMobileShell from '../../../components/admin-mobile/AdminMobileShell';
import { AdminQuickAccess } from '../../../components/admin-mobile/AdminQuickAccess';

const API_URL = window.location.origin || '';

export default function Layout2() {
  const navigate = useNavigate();
  const location = useLocation();
  const { financeiroVisivel, loading: permLoading, can } = usePermV2();
  const FIN_PREFIX = /^\/v2\/(financeiro|dnapay|cobranca|fiscal|tributario|custos|folha|importacao|remessas|retornos)/i;
  const bloqueioFinanceiro = !permLoading && !financeiroVisivel && FIN_PREFIX.test(location.pathname);
  // Perfis do Gestão DNA (sem financeiro) não navegam no shell administrativo V2
  const soGestao = !permLoading && !financeiroVisivel && can('portal_gestao', 'visualizar');
  useEffect(() => {
    if (soGestao) navigate('/gestao', { replace: true });
  }, [soGestao, navigate]);
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);
  const [periodos, setPeriodos] = useState([{ id: '2026', nome: '2026' }, { id: '2025', nome: '2025' }]);
  const [periodoSelecionado, setPeriodoSelecionado] = useState({ id: '2026', nome: '2026' });
  const [segmentos, setSegmentos] = useState([]);
  const [segmentoSelecionado, setSegmentoSelecionado] = useState(null);
  const [notificacoes, setNotificacoes] = useState([]);
  const [pendenciasCount, setPendenciasCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Bloquear perfis restritos de acessar /v2
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const role = (user?.role || '').toLowerCase();
    if (role === 'teacher' || role === 'professor') {
      navigate('/equipe/portal', { replace: true });
      return;
    }
    if (role === 'family' || role === 'guardian' || role === 'responsavel' || role === 'familia') {
      navigate('/familia', { replace: true });
      return;
    }
    if (role === 'portaria') {
      navigate('/dnago/portaria', { replace: true });
      return;
    }

    setUnidades([
      { id: '1', nome: 'Unidade Centro' },
      { id: '2', nome: 'Unidade Norte' }
    ]);

    setSegmentos([
      { id: 'ei', nome: 'Educação Infantil' },
      { id: 'ef1', nome: 'Fundamental I' },
      { id: 'ef2', nome: 'Fundamental II' },
      { id: 'em', nome: 'Ensino Médio' }
    ]);

    setNotificacoes([
      { tipo: 'alerta', titulo: 'Títulos vencendo hoje', mensagem: '23 títulos vencem hoje', tempo: 'Há 5 min', lida: false },
      { tipo: 'info', titulo: 'Fechamento do trimestre', mensagem: '3 professores pendentes', tempo: 'Há 1 hora', lida: false },
      { tipo: 'sucesso', titulo: 'Conciliação concluída', mensagem: '45 pagamentos conciliados', tempo: 'Há 2 horas', lida: true }
    ]);

    // Load real pendencias count from V2 API
    const loadPendencias = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API_URL}/api/v2/central`, { headers });
        if (res.ok) {
          const data = await res.json();
          setPendenciasCount(data.total || 0);
        }
      } catch { setPendenciasCount(0); }
    };
    loadPendencias();
  }, [navigate]);

  const selectors = {
    unidades, unidadeSelecionada, onUnidadeChange: setUnidadeSelecionada,
    periodos, periodoSelecionado, onPeriodoChange: setPeriodoSelecionado,
    segmentos, segmentoSelecionado, onSegmentoChange: setSegmentoSelecionado,
  };

  const content = bloqueioFinanceiro ? (
    <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm"
      data-testid="v2-financeiro-bloqueado">
      <h2 className="text-lg font-bold text-slate-800">Acesso restrito (403)</h2>
      <p className="mt-2 text-sm text-slate-500">
        Seu perfil não tem acesso a informações financeiras. Volte ao Gestão DNA para
        continuar a rotina pedagógica e de secretaria.
      </p>
      <button onClick={() => navigate('/gestao')}
        className="mt-6 rounded-full bg-violet-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-violet-500"
        data-testid="v2-voltar-gestao">Ir para o Gestão DNA</button>
    </div>
  ) : (
    <Outlet context={{
      unidadeSelecionada,
      periodoSelecionado,
      segmentoSelecionado
    }} />
  );

  // Web administrativo no celular/tablet (< 1024px): shell próprio, isolado deste Layout2.
  if (isMobile) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F9F8FA' }}>
        <AdminMobileShell selectors={selectors}>
          <main className="adm-m-main" data-testid="v2-main-content">
            {location.pathname === '/v2/dashboard' && !bloqueioFinanceiro && (
              <AdminQuickAccess onSeeAll={() => window.dispatchEvent(new CustomEvent('dna:adm-open-menu'))} />
            )}
            {content}
          </main>
        </AdminMobileShell>
        <DnaSpotlightV4 />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F8FA' }}>
      <DnaSidebarV4
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        pendencias={pendenciasCount}
      />

      <div className={cn(
        "transition-all duration-300 min-h-screen",
        sidebarCollapsed ? "ml-[72px]" : "ml-[264px]"
      )}>
        <DnaHeaderV4 theme="light" selectors={selectors} />

        <main className="p-8" data-testid="v2-main-content">
          {content}
        </main>
      </div>

      {/* Spotlight V4 (⌘K) — mount once per Layout2 */}
      <DnaSpotlightV4 />
    </div>
  );
}
